import { ErrorTracker } from '@futrli/shared-services';
import React, { ReactNode } from 'react';
import { useLocation, Location } from 'react-router-dom';

import { AnyError } from '../core/types';
import { shouldCleanErrorBoundary } from '../utils/error-boundary-utils';
import { getErrorStatus } from '../utils/utils';
import { Error } from './error';

export interface State {
  error: AnyError | null;
}

interface Props {
  location: Location;
  children: ReactNode;
}

/**
 * Wrapper to catch all errors coming from their children.
 */
export class ErrorBoundary extends React.PureComponent<Props, State> {
  public static getDerivedStateFromError(error: AnyError | null) {
    return { error };
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      error: null
    };
  }

  public componentDidUpdate(prevProps: Props) {
    if (this.props.location !== prevProps.location) {
      if (shouldCleanErrorBoundary(this.props.location)) {
        this.cleanError();
      }
    }
  }

  public componentDidCatch(error: AnyError, info: React.ErrorInfo) {
    ErrorTracker.captureException(error, info);
  }

  public render(): JSX.Element {
    const { error } = this.state;

    if (error) {
      return <Error errorStatus={getErrorStatus(error)} />;
    }

    return <React.Fragment>{this.props.children}</React.Fragment>;
  }

  private cleanError = (): void => {
    this.setState({ error: null });
  };
}

export const ErrorBoundaryWR = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  return <ErrorBoundary location={location}>{children}</ErrorBoundary>;
};
